import { Product } from "@shared/types/ProductTypes.interface";
import { Specifications } from "../Specifications/Specifications.component";
import { HMF_Text } from "@shared/components/Atoms/Text/Text.component";
import { useTranslations } from "@shared/context/useTranslations";
import { cn } from "@shared/helpers/classNames.helper";
import S from "./ProductDetails.module.scss";
import { useCrane } from "@shared/context/CraneContext";
import { useCallback, useEffect, useState } from "react";
import { CraneChildItem } from "@shared/types/CraneTypes";
import { CraneChildItemList } from "@shared/components/Organisms/CraneChildItemList/CraneChildItemList.components";

interface ProductDetailsProps {
    product: Product;
}

enum ProductDetailsSection {
    OneColumn = "one-column",
    TwoColumns = "two-columns",
    Full = "full",
    None = "none"
}

export const ProductDetails = ({ product }: ProductDetailsProps) => {
    const { getTranslation } = useTranslations();
    const { isCraneMode, craneProduct } = useCrane();
    const [childItems, setChildItems] = useState<CraneChildItem[]>([]);
    const [detailsSection, setDetailsSection] = useState<ProductDetailsSection>(
        ProductDetailsSection.None
    );
    const specifications = product.specifications.filter(
        (specification) => specification.value !== "" && specification.value !== "0"
    );
    
    useEffect(() => {
        if (isCraneMode && craneProduct) {
            setChildItems(craneProduct.children || []);
        }
    }, [product, isCraneMode, craneProduct]);

    const getDetailsSection = useCallback((): ProductDetailsSection => {
        if (product.longDescription !== "") {
            return ProductDetailsSection.Full;
        }
        if (specifications.length > 0 && childItems.length > 0) {
            return ProductDetailsSection.TwoColumns;
        }
        if (specifications.length > 0 || childItems.length > 0) {
            return ProductDetailsSection.OneColumn;
        }
        return ProductDetailsSection.None;
    },[childItems.length, product.longDescription, specifications.length]);
    
    useEffect(() => {
        setDetailsSection(getDetailsSection());
    }, [getDetailsSection]);

    return detailsSection !== ProductDetailsSection.None ? (
        <div className={S.productDetails}>
            <div className={cn(S.productDetail, S[detailsSection])}>
                {isCraneMode && childItems.length > 0 && (
                <div className={S.productDetailChildren}>
                    <h3>{getTranslation("pdp.headings.child-items")}</h3>
                    <CraneChildItemList items={childItems} isPdP={true} />
                </div>
                )}

                {/* Specifications */}
                {specifications.length > 0 && (
                    <div className={S.productDetailSpecifications}>
                        <h3>{getTranslation("pdp.headings.specifications")}</h3>
                        <Specifications {...product} />
                    </div>
                )}

                {/* Separator */}
                {specifications.length > 0 && product.longDescription !== "" && (
                    <div className={S.separator}></div>
                )}

                {/* Long description */}
                {product.longDescription !== "" && (
                    <div className={S.productDetail}>
                        <h3 id="long-description-header">
                            {getTranslation("pdp.headings.description")}
                        </h3>
                        <HMF_Text html={product?.longDescription} />
                    </div>
                )}
            </div>
        </div>
    ) : null;
};
